import { faComputer } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Pako from "pako";
import { useAppDispatch } from "../../../../../app/hooks";
import { AppDispatch, store } from "../../../../../app/store";
import { seedAll } from "../../../../../common/generateUniqueId";
import SaveFormat from "../../../../../common/save/SaveFormat";
import { OneDrivePicker } from "../../../../editor/components/oneDrive/OneDrivePicker";
import { loadEditorState, resetEditor } from "../../../../editor/editorSlice";
import {
	fetchLogicsConfiguration,
	resetLogics,
} from "../../../../logics/logicsSlice";
import { resetMenuManager } from "../../../../menuManager/menuManagerSlice";
import {
	closeWindow,
	closeWindowsExcept,
	resetWindowManager,
	WindowTypeAndPropsDefinition,
} from "../../../windowManagerSlice";
import css from "./LoadProjectWindow.module.css";

export interface LoadProjectWindowProps {}

export type LoadProjectWindowTypeAndProps = WindowTypeAndPropsDefinition<
	"loadProject",
	LoadProjectWindowProps
>;

export function parsePpfContent(content: ArrayBuffer | Uint8Array): SaveFormat {
	const bytes =
		content instanceof Uint8Array ? content : new Uint8Array(content);

	let json: string;
	try {
		json = Pako.inflate(bytes, { to: "string" });
	} catch {
		json = new TextDecoder().decode(bytes);
	}

	const saveFormat = JSON.parse(json) as SaveFormat;
	saveFormat.editor.blockPreview = null;
	return saveFormat;
}

function hasUsableCommitRef(
	commitRef: SaveFormat["logicsCommitRef"] | undefined,
): commitRef is SaveFormat["logicsCommitRef"] {
	return Boolean(
		commitRef &&
			typeof commitRef.name === "string" &&
			commitRef.name.length > 0 &&
			typeof commitRef.hash === "string" &&
			commitRef.hash.length > 0,
	);
}

export function applyLoadedProject(
	dispatch: AppDispatch,
	saveFormat: SaveFormat,
) {
	dispatch(loadEditorState(saveFormat.editor));

	if (saveFormat.idMap) {
		seedAll(saveFormat.idMap);
	}

	if (hasUsableCommitRef(saveFormat.logicsCommitRef)) {
		const logicsStatus = store.getState().logics.logicsStatus;
		const alreadyLoaded =
			logicsStatus.code === "succeeded" &&
			logicsStatus.logics.commit.name === saveFormat.logicsCommitRef.name &&
			logicsStatus.logics.commit.hash === saveFormat.logicsCommitRef.hash;

		if (!alreadyLoaded) {
			dispatch(
				fetchLogicsConfiguration({
					...saveFormat.logicsCommitRef,
					migrate: false,
				}),
			);
		}
	}

	dispatch(closeWindowsExcept("chat"));
}

export function openLoadedProject(
	dispatch: AppDispatch,
	saveFormat: SaveFormat,
	windowId?: string,
) {
	dispatch(resetEditor());
	dispatch(resetWindowManager());
	dispatch(resetMenuManager());
	dispatch(resetLogics());
	applyLoadedProject(dispatch, saveFormat);

	if (windowId) {
		dispatch(closeWindow(windowId));
	}
}

export function loadProject(
	cb: (saveFormat: SaveFormat) => void,
	content?: ArrayBuffer | Uint8Array,
) {
	const applyContent = (buffer: ArrayBuffer | Uint8Array) => {
		const saveFormat = parsePpfContent(buffer);
		console.log({ saveFormat });
		cb(saveFormat);
	};

	if (content) {
		applyContent(content);
		return;
	}

	const input = document.createElement("input");
	input.type = "file";
	input.accept = ".ppf";

	input.onchange = (event) => {
		const file = (event.target as HTMLInputElement).files?.[0];

		if (!file) {
			console.warn(`Error reading file.`);
			return;
		}

		const reader = new FileReader();
		reader.readAsArrayBuffer(file);

		reader.onload = (event) => {
			const fileContent = event.target?.result;

			if (!fileContent) {
				console.warn(`Error reading file contents.`);
				return;
			}

			if (typeof fileContent === "string") {
				console.warn(`File content read as string.`);
				return;
			}

			applyContent(fileContent);
		};
	};

	input.click();
}

export function LoadProjectWindow(
	props: LoadProjectWindowProps & { id: string },
) {
	const dispatch = useAppDispatch();

	return (
		<div className={css.content}>
			<div id="loadproject" className={css.content}>
				<div className={css.confirmButtonChunk}>
					<button
						className={css.confirmButton}
						onClick={() => {
							loadProject((saveFormat) => {
								openLoadedProject(dispatch, saveFormat, props.id);
							});
						}}
					>
						<FontAwesomeIcon icon={faComputer} /> Computer
					</button>
					<OneDrivePicker
						modalId={props.id}
						type="modal"
						disabled={false}
						newName={null}
					/>
				</div>
			</div>
		</div>
	);
}
